import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";

// Matches the palette used in App.jsx so the sign-in screen feels part of the app.
const BRAND = {
  navy: "#1C2B3A",
  slate: "#4A6FA5",
  sage: "#7A9E87",
  sagePale: "#EBF2ED",
  gray: "#F4F5F7",
  grayMid: "#9BA8B0",
  border: "#DDE3E8",
  white: "#FFFFFF",
  ink: "#1C2B3A",
  muted: "#6B7A85",
  red: "#C0392B",
};

const st = {
  wrap: {
    minHeight: "100vh",
    background: BRAND.gray,
    fontFamily: "'Inter','Helvetica Neue',sans-serif",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    boxSizing: "border-box",
  },
  card: {
    background: BRAND.white,
    borderRadius: 16,
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 4px 24px rgba(28,43,58,0.10)",
    width: "100%",
    maxWidth: 380,
    padding: "28px 24px 24px",
    boxSizing: "border-box",
  },
  brandRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 6 },
  logo: {
    width: 44,
    height: 44,
    borderRadius: 11,
    background: BRAND.navy,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    flexShrink: 0,
  },
  appName: { fontSize: 22, fontWeight: 800, color: BRAND.navy, letterSpacing: "-0.5px", margin: 0 },
  tagline: { fontSize: 12, color: BRAND.muted, margin: "2px 0 0" },
  heading: { fontSize: 16, fontWeight: 700, color: BRAND.navy, margin: "22px 0 4px" },
  sub: { fontSize: 13, color: BRAND.muted, margin: "0 0 18px", lineHeight: 1.5 },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: BRAND.muted,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 5,
    display: "block",
  },
  input: {
    width: "100%",
    padding: "11px 13px",
    borderRadius: 8,
    border: `1.5px solid ${BRAND.border}`,
    fontSize: 14,
    color: BRAND.ink,
    background: BRAND.white,
    boxSizing: "border-box",
    outline: "none",
    marginBottom: 14,
  },
  primary: (disabled) => ({
    width: "100%",
    background: disabled ? BRAND.grayMid : BRAND.navy,
    color: BRAND.white,
    border: "none",
    borderRadius: 8,
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 700,
    cursor: disabled ? "default" : "pointer",
    marginTop: 4,
  }),
  toggleRow: { textAlign: "center", marginTop: 18, fontSize: 13, color: BRAND.muted },
  toggleBtn: {
    background: "none",
    border: "none",
    color: BRAND.slate,
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
    padding: 0,
  },
  error: {
    background: "#FDEAED",
    color: BRAND.red,
    border: "1px solid #F5C6CB",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13,
    marginBottom: 14,
    lineHeight: 1.4,
  },
  notice: {
    background: BRAND.sagePale,
    color: BRAND.sage,
    border: `1px solid ${BRAND.sage}`,
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13,
    marginBottom: 14,
    lineHeight: 1.4,
  },
  splash: {
    minHeight: "100vh",
    background: BRAND.gray,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Inter','Helvetica Neue',sans-serif",
    color: BRAND.muted,
    fontSize: 14,
  },
};

// Turn Supabase's raw auth errors into friendly, parent-facing copy.
function friendlyError(message = "") {
  const m = message.toLowerCase();
  if (m.includes("invalid login credentials")) return "Email or password is incorrect.";
  if (m.includes("already registered")) return "That email already has an account — try signing in instead.";
  if (m.includes("email not confirmed")) return "Please confirm your email first — check your inbox for the link.";
  if (m.includes("password should be at least")) return "Password needs to be at least 6 characters.";
  if (m.includes("unable to validate email")) return "That doesn't look like a valid email address.";
  if (m.includes("rate limit") || m.includes("too many")) return "Too many attempts — please wait a moment and try again.";
  if (m.includes("same as the old") || m.includes("different from the old")) return "Choose a password you haven't used before.";
  return message || "Something went wrong — please try again.";
}

function Logo() {
  return (
    <div style={st.logo}>
      <img
        src="/apple-touch-icon.png"
        alt="MumHQ"
        style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 11 }}
        onError={(e) => {
          e.target.style.display = "none";
          if (e.target.parentNode) e.target.parentNode.innerHTML = "🏡";
        }}
      />
    </div>
  );
}

function AuthForm() {
  const [mode, setMode] = useState("signin"); // "signin" | "signup" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const submit = async () => {
    setError("");
    setNotice("");
    const cleanEmail = email.trim();
    if (mode === "forgot") {
      if (!cleanEmail) { setError("Enter the email you signed up with."); return; }
      setBusy(true);
      try {
        // Supabase emails a one-time link back to this site; AuthGate spots the
        // PASSWORD_RECOVERY event when it's opened and shows the "new password" form.
        const { error: err } = await supabase.auth.resetPasswordForEmail(cleanEmail, { redirectTo: window.location.origin });
        if (err) setError(friendlyError(err.message));
        // Same message whether or not the account exists, so emails can't be probed.
        else { setNotice("If there's an account for that email, we've sent a link to reset your password. Check your inbox (and spam)."); setMode("signin"); }
      } catch (e) {
        setError("Couldn't reach the server — check your connection and try again.");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!cleanEmail || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        const { data, error: err } = await supabase.auth.signUp({
          email: cleanEmail,
          password,
        });
        if (err) {
          setError(friendlyError(err.message));
        } else if (data.session) {
          // Email confirmation is OFF — the auth listener will let them straight in.
        } else {
          // Email confirmation is ON — no session until they click the link.
          setNotice("Almost there! Check your inbox for a confirmation link, then sign in.");
          setMode("signin");
        }
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({
          email: cleanEmail,
          password,
        });
        if (err) setError(friendlyError(err.message));
        // On success, onAuthStateChange in AuthGate swaps in the app automatically.
      }
    } catch (e) {
      setError("Couldn't reach the server — check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !busy) submit();
  };

  return (
    <div style={st.wrap}>
      <div style={st.card}>
        <div style={st.brandRow}>
          <Logo />
          <div>
            <h1 style={st.appName}>MumHQ</h1>
            <p style={st.tagline}>Family life, sorted.</p>
          </div>
        </div>

        <h2 style={st.heading}>{mode === "signin" ? "Welcome back" : mode === "signup" ? "Create your account" : "Reset your password"}</h2>
        <p style={st.sub}>
          {mode === "signin"
            ? "Sign in to see your family's plan on any device."
            : mode === "signup"
            ? "One account keeps your plan synced across all your devices."
            : "Enter your email and we'll send you a link to choose a new password."}
        </p>

        {error && <div style={st.error}>{error}</div>}
        {notice && <div style={st.notice}>{notice}</div>}

        <label style={st.label} htmlFor="auth-email">Email</label>
        <input
          id="auth-email"
          style={st.input}
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKeyDown}
        />

        {mode !== "forgot" && (
          <>
            <label style={st.label} htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              style={st.input}
              type="password"
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </>
        )}

        {mode === "signin" && (
          <div style={{ textAlign: "right", margin: "-6px 0 12px" }}>
            <button style={{ ...st.toggleBtn, fontWeight: 600, fontSize: 12 }}
              onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}>
              Forgot password?
            </button>
          </div>
        )}

        <button style={st.primary(busy)} onClick={submit} disabled={busy}>
          {busy ? "Please wait…" : mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
        </button>

        <div style={st.toggleRow}>
          {mode === "signin" ? "New to MumHQ? " : mode === "signup" ? "Already have an account? " : "Remembered it? "}
          <button
            style={st.toggleBtn}
            onClick={() => {
              setMode(mode === "signin" ? "signup" : "signin");
              setError("");
              setNotice("");
            }}
          >
            {mode === "signin" ? "Create an account" : "Sign in"}
          </button>
        </div>

        {mode === "signup" && (
          <p style={{ fontSize: 11, color: BRAND.muted, textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
            By creating an account you agree to our{" "}
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.slate }}>privacy policy</a>.
          </p>
        )}
        <div style={{ textAlign: "center", marginTop: mode === "signup" ? 6 : 14 }}>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: BRAND.muted }}>Privacy policy</a>
        </div>
      </div>
    </div>
  );
}

// Shown after a parent opens the "reset your password" link from their email.
function NewPasswordForm({ onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (pw.length < 6) { setError("Password needs to be at least 6 characters."); return; }
    if (pw !== pw2) { setError("Those passwords don't match."); return; }
    setBusy(true);
    const { error: err } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (err) { setError(friendlyError(err.message)); return; }
    onDone();
  };

  return (
    <div style={st.wrap}>
      <div style={st.card}>
        <div style={st.brandRow}>
          <Logo />
          <div>
            <h1 style={st.appName}>MumHQ</h1>
            <p style={st.tagline}>Family life, sorted.</p>
          </div>
        </div>
        <h2 style={st.heading}>Choose a new password</h2>
        <p style={st.sub}>You'll stay signed in on this device.</p>
        {error && <div style={st.error}>{error}</div>}
        <label style={st.label} htmlFor="new-password">New password</label>
        <input id="new-password" style={st.input} type="password" autoComplete="new-password"
          placeholder="At least 6 characters" value={pw} onChange={(e) => setPw(e.target.value)} />
        <label style={st.label} htmlFor="new-password-2">Confirm new password</label>
        <input id="new-password-2" style={st.input} type="password" autoComplete="new-password"
          value={pw2} onChange={(e) => setPw2(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !busy) submit(); }} />
        <button style={st.primary(busy)} onClick={submit} disabled={busy}>
          {busy ? "Please wait…" : "Save new password"}
        </button>
      </div>
    </div>
  );
}

export default function AuthGate({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // True while the parent is finishing a password reset (opened the emailed link).
  const [recovering, setRecovering] = useState(
    typeof window !== "undefined" && /type=recovery/.test(window.location.hash)
  );

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === "PASSWORD_RECOVERY") setRecovering(true);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    // onAuthStateChange fires with a null session and the gate shows the form again.
  };

  if (loading) {
    return <div style={st.splash}>Loading MumHQ…</div>;
  }

  if (recovering && session) {
    return (
      <NewPasswordForm
        onDone={() => {
          setRecovering(false);
          // Tidy the one-time token out of the address bar.
          try { window.history.replaceState(null, "", window.location.pathname); } catch (e) {}
        }}
      />
    );
  }

  if (!session) {
    return <AuthForm />;
  }

  // Render-prop: App.jsx calls this as {(user, signOut) => <MumHQ ... />}
  return children(session.user, signOut);
}
